// src/pages/admin/AdminCloud.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  Field, TextInput, TextArea, PrimaryBtn, GhostBtn,
  Toast, AdminCard, AdminHeading, ConfirmDialog,
} from '../../components/AdminUI';
import RichTextEditor from '../../components/RichTextEditor';

function blankProvider() {
  return {
    id: Date.now().toString(),
    name: '',
    url: '',
    isReferral: false,
    showLoadActivity: false,
    description: '',
  };
}

function ProviderEditor({ provider, onChange, onDelete, index }) {
  const providerRef = useRef(provider);
  providerRef.current = provider;
  const set = (k, v) => onChange({ ...providerRef.current, [k]: v });
  return (
    <AdminCard style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', letterSpacing: 1 }}>
          PROVIDER {String(index + 1).padStart(2, '0')}
        </span>
        <PrimaryBtn onClick={onDelete} danger small>Remove</PrimaryBtn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
        <Field label="Provider name" hint="e.g. Runpod, Vast.ai, Lambda Labs">
          <TextInput value={provider.name} onChange={v => set('name', v)} placeholder="Runpod" />
        </Field>
        <Field label="URL / referral link" hint="Main link or referral URL">
          <TextInput value={provider.url || ''} onChange={v => set('url', v)} placeholder="https://runpod.io/?ref=…" mono />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--sand-muted)' }}>
          <input type="checkbox" checked={provider.isReferral || false}
            onChange={e => set('isReferral', e.target.checked)}
            style={{ accentColor: 'var(--sand-amber)', width: 15, height: 15 }} />
          Referral link (shows disclaimer)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--sand-muted)' }}>
          <input type="checkbox" checked={provider.showLoadActivity || false}
            onChange={e => set('showLoadActivity', e.target.checked)}
            style={{ accentColor: 'var(--sand-amber)', width: 15, height: 15 }} />
          Show "GPU load activity" link (Runpod only)
        </label>
      </div>

      <Field label="Description" hint="Supports bold, italic, headings, links, images, YouTube. Great for comparison tables.">
        <RichTextEditor value={provider.description} onChange={v => set('description', v)} />
      </Field>
    </AdminCard>
  );
}

export default function AdminCloud() {
  const [cfg, setCfg]         = useState({ intro: '', providers: [], loadPattern: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState({ msg: '', type: 'success' });
  const [confirmIdx, setConfirmIdx] = useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  }

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'config', 'cloud'));
        if (snap.exists()) setCfg(prev => ({ ...prev, ...snap.data() }));
      } catch (e) {
        showToast('Failed to load config.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  function updateProvider(idx, updated) {
    setCfg(prev => {
      const next = [...(prev.providers || [])];
      next[idx] = updated;
      return { ...prev, providers: next };
    });
  }
  function removeProvider(idx) {
    setCfg(prev => ({
      ...prev,
      providers: (prev.providers || []).filter((_, i) => i !== idx),
    }));
    setConfirmIdx(null);
  }
  function addProvider() {
    setCfg(prev => ({
      ...prev,
      providers: [...(prev.providers || []), blankProvider()],
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'cloud'), { ...cfg, updatedAt: serverTimestamp() });
      showToast('Cloud config saved.');
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ fontSize: 13, color: 'var(--sand-hint)' }}>Loading…</p>;

  return (
    <div>
      <AdminHeading>Cloud tab config</AdminHeading>
      <p style={{ fontSize: 12, color: 'var(--sand-hint)', fontStyle: 'italic', marginBottom: 20 }}>
        Controls the public Cloud options tab — intro text, provider entries, and the Runpod load activity pattern.
      </p>

      {/* Intro */}
      <AdminCard>
        <Field label="Intro text" hint="Optional overview shown above the provider list. Supports rich text.">
          <RichTextEditor value={cfg.intro || ''} onChange={v => set('intro', v)} />
        </Field>
      </AdminCard>

      {/* Provider list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)' }}>
          Providers ({(cfg.providers || []).length})
        </h3>
        <GhostBtn onClick={addProvider}>+ Add provider</GhostBtn>
      </div>

      {(cfg.providers || []).length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--sand-hint)', fontStyle: 'italic', marginBottom: 16 }}>
          No providers yet.
        </p>
      ) : (
        (cfg.providers || []).map((p, idx) => (
          <ProviderEditor
            key={p.id}
            provider={p}
            index={idx}
            onChange={updated => updateProvider(idx, updated)}
            onDelete={() => setConfirmIdx(idx)}
          />
        ))
      )}

      {/* Load activity pattern */}
      <AdminCard>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)', marginBottom: 8 }}>
          Runpod Load Activity Pattern
        </h3>
        <p style={{ fontSize: 12, color: 'var(--sand-muted)', marginBottom: 12, lineHeight: 1.6 }}>
          7 rows (Sun–Sat) × 24 columns (UTC hours 0–23). Values: 0=Quiet 1=Light 2=Moderate 3=Busy 4=Overloaded. Leave blank for built-in default.
        </p>
        <Field label="Pattern JSON" hint='e.g. [[0,1,2,...],[...]]  — leave blank for default'>
          <TextArea
            value={cfg.loadPattern ? JSON.stringify(cfg.loadPattern) : ''}
            onChange={v => {
              if (!v.trim()) { set('loadPattern', null); return; }
              try { set('loadPattern', JSON.parse(v)); } catch {}
            }}
            placeholder='Leave blank to use default, or paste [[0,1,2,...],...]'
            rows={4} mono
          />
        </Field>
      </AdminCard>

      <div style={{ marginTop: 20 }}>
        <PrimaryBtn onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save all changes'}
        </PrimaryBtn>
      </div>

      {confirmIdx !== null && (
        <ConfirmDialog
          message={`Remove provider "${cfg.providers[confirmIdx]?.name || 'this provider'}"?`}
          onConfirm={() => removeProvider(confirmIdx)}
          onCancel={() => setConfirmIdx(null)}
        />
      )}
      <Toast message={toast.msg} type={toast.type} />
    </div>
  );
}
